import React, { useEffect, useRef, useState } from 'react';
import { assetsService } from '../../services/assets.service';
import AssetMiniChart from './AssetMiniChart';
import { fmtQuotePx, quoteFromFeed } from './quoteFromFeed';

export const QUOTE_POLL_MS = 3000;

const AssetLiveQuote = ({ assetId, fallback, chartHeightClass = 'h-32' }) => {
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;
  const [quote, setQuote] = useState(() => quoteFromFeed(fallback));

  useEffect(() => {
    setQuote(quoteFromFeed(fallbackRef.current));
  }, [assetId]);

  useEffect(() => {
    if (!assetId) return undefined;
    let cancelled = false;

    const load = () => {
      assetsService
        .getAssetPrice(assetId)
        .then((price) => {
          if (cancelled) return;
          setQuote(quoteFromFeed(price) || quoteFromFeed(fallbackRef.current));
        })
        .catch(() => {
          if (cancelled) return;
          setQuote(quoteFromFeed(fallbackRef.current));
        });
    };

    load();
    const timer = setInterval(load, QUOTE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [assetId]);

  if (!assetId) return null;

  const quoteUp = quote?.changePercent != null && quote.changePercent >= 0;

  return (
    <div className="rounded-lg border border-ink-200 bg-ink-50/60 p-3 space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400">Last</p>
          <p className="text-lg font-semibold text-ink-900 tnum font-mono">{fmtQuotePx(quote?.last)}</p>
        </div>
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400">Bid</p>
          <p className="text-lg font-semibold text-success-700 tnum font-mono">{fmtQuotePx(quote?.bid)}</p>
        </div>
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400">Ask</p>
          <p className="text-lg font-semibold text-danger-700 tnum font-mono">{fmtQuotePx(quote?.ask)}</p>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3">
        {quote?.spread != null ? (
          <p className="text-2xs text-ink-400 tnum">Spread {fmtQuotePx(quote.spread)}</p>
        ) : (
          <span />
        )}
        {quote?.changePercent != null && (
          <p className={`text-sm font-medium tnum ${quoteUp ? 'text-success-600' : 'text-danger-600'}`}>
            {quoteUp ? '+' : ''}
            {Number(quote.changePercent).toFixed(2)}%
          </p>
        )}
      </div>
      <AssetMiniChart
        assetId={assetId}
        symbol={fallback?.symbol}
        market={fallback?.marketSymbol || fallback?.market || fallback?.countryCode}
        chartHeightClass={chartHeightClass}
      />
    </div>
  );
};

export default AssetLiveQuote;
